// #ux-redesign-start: showcase oculto del Design System (Paso 2)
// Vista interna para inspeccionar las primitivas del DS en light/dark.
// Acceso por URL directa: /admin/_ds-preview (no aparece en sidebar).
//
// NO se traduce (es herramienta interna).
// NO se incluye en producción si querés — basta agregar invisible:true + sin
// permission en routes.js (ya lo hacemos así).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useMemo } from 'react';
import {
  Button,
  Input,
  Textarea,
  Select,
  Badge,
  Card,
  PageContainer,
  SectionCard,
  EmptyState,
  DataTable,
  Modal,
  Drawer,
  ConfirmDialog,
  Spinner,
  LoadingBlock,
  LoadingOverlay,
} from 'components/ds';
import { useTheme } from 'contexts/ThemeContext';
import useResponsive from 'hooks/useResponsive';

const DsPreview = () => {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { isMobile, isTablet, isDesktop, bp, width } = useResponsive();
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const sampleData = useMemo(() => [
    { id: 1, date: '2026-05-10', patient: 'Müller, Hans',   type: 'Labor',   status: 'Endbefund', comment: 'Offen' },
    { id: 2, date: '2026-05-09', patient: 'Schmidt, Anna',  type: 'LZ-EKG',  status: 'Offen',     comment: 'Gesendet' },
    { id: 3, date: '2026-05-08', patient: 'Bauer, Klaus',   type: 'Spirometrie', status: 'Endbefund', comment: 'Patient ✓' },
    { id: 4, date: '2026-05-07', patient: 'Klein, Petra',   type: 'Labor',   status: 'Gesendet',  comment: '—' },
  ], []);

  const columns = useMemo(() => [
    { Header: 'Datum', accessor: 'date' },
    { Header: 'Patient', accessor: 'patient' },
    { Header: 'Typ', accessor: 'type' },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => {
        const variant = value === 'Endbefund' ? 'success'
          : value === 'Offen' ? 'warning'
          : value === 'Gesendet' ? 'info' : 'neutral';
        return <Badge variant={variant} dot>{value}</Badge>;
      },
    },
    { Header: 'Kommentar', accessor: 'comment' },
    {
      Header: '',
      id: 'actions',
      Cell: () => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" size="sm">öffnen</Button>
        </div>
      ),
    },
  ], []);

  return (
    <PageContainer
      title="Design System Preview"
      subtitle="Vista interna · acceder por URL · no esta en sidebar"
      breadcrumb={[{ label: 'Intern' }, { label: 'DS Preview' }]}
      actions={
        <>
          <Button variant="tertiary" size="sm" onClick={() => setTheme('light')}>Light</Button>
          <Button variant="tertiary" size="sm" onClick={() => setTheme('dark')}>Dark</Button>
        </>
      }
    >
      <SectionCard title="Theme & Responsive" description="Estado actual del ThemeProvider y useResponsive">
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <KV label="theme" value={theme} />
          <KV label="resolvedTheme" value={resolvedTheme} />
          <KV label="breakpoint" value={bp} />
          <KV label="width" value={`${width}px`} />
          <KV label="isMobile" value={String(isMobile)} />
          <KV label="isTablet" value={String(isTablet)} />
          <KV label="isDesktop" value={String(isDesktop)} />
        </div>
      </SectionCard>

      <SectionCard title="Buttons" description="Variantes y tamaños">
        <Row label="Primary (CTA principal)">
          <Button size="sm" variant="primary">Speichern</Button>
          <Button size="md" variant="primary">Speichern</Button>
          <Button size="lg" variant="primary">Speichern</Button>
          <Button size="md" variant="primary" loading>Lädt</Button>
          <Button size="md" variant="primary" disabled>Deaktiviert</Button>
        </Row>
        <Row label="Secondary (Cancel, neutro)">
          <Button size="sm" variant="secondary">Abbrechen</Button>
          <Button size="md" variant="secondary">Abbrechen</Button>
          <Button size="lg" variant="secondary">Abbrechen</Button>
        </Row>
        <Row label="Tertiary (link, ghost)">
          <Button size="sm" variant="tertiary">Mehr ansehen</Button>
          <Button size="md" variant="tertiary">Mehr ansehen</Button>
        </Row>
        <Row label="Danger (destructivo)">
          <Button size="sm" variant="danger">Löschen</Button>
          <Button size="md" variant="danger">Löschen</Button>
        </Row>
      </SectionCard>

      <SectionCard title="Fields" description="Inputs, Textarea, Select del DS">
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          <Input label="E-Mail" placeholder="name@klinik.de" required />
          <Input label="Telefon" hint="optional" placeholder="+49 ..." />
          <Input label="Passwort" type="password" helper="Mindestens 12 Zeichen." />
          <Input label="Mit Fehler" error="Pflichtfeld" value="" onChange={() => {}} />
          <Input label="Deaktiviert" disabled value="readonly value" />
          <Select
            label="Sprache"
            options={[
              { value: 'de', label: 'Deutsch' },
              { value: 'en', label: 'English' },
              { value: 'es', label: 'Español' },
              { value: 'ru', label: 'Русский' },
            ]}
            placeholder="Auswählen…"
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <Textarea label="Notiz" placeholder="Optionale Notiz für die Derivation…" rows={3} />
        </div>
      </SectionCard>

      <SectionCard title="Badges" description="Estados — soft, no solid">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Badge variant="success" dot>Endbefund</Badge>
          <Badge variant="warning" dot>Offen</Badge>
          <Badge variant="danger" dot>Fehler</Badge>
          <Badge variant="info" dot>TP</Badge>
          <Badge variant="priority" dot>Eilig</Badge>
          <Badge variant="neutral">Entwurf</Badge>
          <Badge variant="success" size="sm">sm</Badge>
          <Badge variant="warning" size="sm">sm</Badge>
        </div>
      </SectionCard>

      <SectionCard title="Cards & Containers" description="Card / PageContainer / SectionCard">
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <Card padding="md" elevation={0}>
            <strong>elevation-0</strong>
            <p style={{ margin: '8px 0 0', color: 'var(--color-text-secondary)' }}>Flat con borde</p>
          </Card>
          <Card padding="md" elevation={1} border={false}>
            <strong>elevation-1</strong>
            <p style={{ margin: '8px 0 0', color: 'var(--color-text-secondary)' }}>Sin borde</p>
          </Card>
          <Card padding="md" elevation={2} border={false}>
            <strong>elevation-2</strong>
            <p style={{ margin: '8px 0 0', color: 'var(--color-text-secondary)' }}>Hover sutil</p>
          </Card>
          <Card padding="md" elevation={3} border={false}>
            <strong>elevation-3</strong>
            <p style={{ margin: '8px 0 0', color: 'var(--color-text-secondary)' }}>Dropdowns</p>
          </Card>
        </div>
      </SectionCard>

      <SectionCard title="DataTable" description="Wrapper de react-table con sort + paginación + density">
        <DataTable
          columns={columns}
          data={sampleData}
          density="regular"
          pagination={{
            page: 0,
            lastPage: 0,
            pageSize: 15,
            total: sampleData.length,
            onPageChange: () => {},
            onPageSizeChange: () => {},
          }}
        />
      </SectionCard>

      <SectionCard title="Empty State" description="Cuando no hay datos / cuando no hay seleccionado">
        <EmptyState
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9h18M9 3v18" />
            </svg>
          }
          title="Keine ausstehenden Studien"
          description="Sobald HL7-Daten eingehen, erscheinen sie hier automatisch."
          action={<Button variant="primary">Manuell hochladen</Button>}
        />
      </SectionCard>

      <SectionCard title="Overlays" description="Modal, Drawer, ConfirmDialog">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={() => setModalOpen(true)}>Modal abrir</Button>
          <Button variant="secondary" onClick={() => setDrawerOpen(true)}>Drawer abrir</Button>
          <Button variant="danger" onClick={() => setConfirmOpen(true)}>Confirm dialog</Button>
        </div>

        <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Modal de ejemplo" size="md">
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            En mobile (≤768px) este modal pasa automáticamente a bottom-sheet.
          </p>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Abbrechen</Button>
            <Button variant="primary" onClick={() => setModalOpen(false)}>Bestätigen</Button>
          </Modal.Footer>
        </Modal>

        <Drawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title="Drawer · Detalle de estudio"
          side="right"
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={() => setDrawerOpen(false)}>Schließen</Button>
              <Button variant="primary">Zuweisen</Button>
            </>
          }
        >
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            En mobile este drawer pasa a full-screen. Ideal para reemplazar modales de detalle
            pesados (AssignDoctor, KitDetail, DerivationHistory) por un solo panel con tabs.
          </p>
        </Drawer>

        <ConfirmDialog
          isOpen={confirmOpen}
          title="Studie löschen?"
          description="Diese Aktion kann nicht rückgängig gemacht werden."
          variant="danger"
          confirmLabel="Löschen"
          cancelLabel="Abbrechen"
          onConfirm={() => new Promise((r) => setTimeout(() => { setConfirmOpen(false); r(); }, 800))}
          onCancel={() => setConfirmOpen(false)}
        />
      </SectionCard>

      <SectionCard
        title="Spinners (ECG)"
        description="Sistema de carga unificado · reemplaza LoadingLocal / LoadingGlobal / Reactstrap Spinner"
      >
        <Row label="Standalone (sm / md / lg)">
          <Spinner size="sm" />
          <Spinner size="md" />
          <Spinner size="lg" />
        </Row>
        <Row label="Con texto">
          <Spinner size="sm" label="Cargando paciente…" />
        </Row>
        <Row label="En botón (loading)">
          <Button variant="primary" loading>Procesando…</Button>
          <Button variant="secondary" loading>Speichert</Button>
        </Row>
        <Row label="LoadingBlock (reemplazo de LoadingLocal)">
          <div style={{ flex: 1, border: '1px dashed var(--color-border-subtle)', borderRadius: 6 }}>
            <LoadingBlock />
          </div>
          <div style={{ flex: 1, border: '1px dashed var(--color-border-subtle)', borderRadius: 6 }}>
            <LoadingBlock label="Lade Studien…" />
          </div>
        </Row>
        <Row label="LoadingOverlay contained">
          <div style={{ position: 'relative', width: 320, height: 140, border: '1px dashed var(--color-border-subtle)', borderRadius: 6, padding: 12 }}>
            <p style={{ color: 'var(--color-text-tertiary)', fontSize: 12, margin: 0 }}>
              Contenido bajo el overlay (fondo semitransparente + blur, theme-aware).
            </p>
            <LoadingOverlay mode="contained" size="md" label="Lade…" />
          </div>
        </Row>
        <Row label="LoadingOverlay fullscreen (3s)">
          <Button
            variant="secondary"
            onClick={() => {
              setOverlayOpen(true);
              setTimeout(() => setOverlayOpen(false), 3000);
            }}
          >
            Overlay 3s ansehen
          </Button>
        </Row>
        {overlayOpen && <LoadingOverlay label="Lade Anwendung…" />}
      </SectionCard>
    </PageContainer>
  );
};

const Row = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 0', borderBottom: '1px solid var(--color-border-subtle)' }}>
    <div style={{ minWidth: 200, fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>{label}</div>
    {children}
  </div>
);

const KV = ({ label, value }) => (
  <div style={{ padding: 12, backgroundColor: 'var(--color-surface-sunken)', borderRadius: 6 }}>
    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontFamily: 'var(--font-family-mono)', fontSize: 14, color: 'var(--color-text-primary)', marginTop: 4 }}>{value}</div>
  </div>
);

export default DsPreview;
// #ux-redesign-end
